import { useCallback, useEffect, useState } from 'react';
import { waitForMenu, getAllMicroFrontendEntries } from '@/lib/menu-utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface MFManifest {
  id?: string;
  name?: string;
  metaData?: {
    remoteEntry?: {
      name?: string;
      path?: string;
      type?: string;
    };
    buildInfo?: { buildVersion?: string; buildName?: string };
  };
  exposes?: unknown[];
  shared?: unknown[];
}

interface ManifestCheck {
  route: string;
  url: string;
  manifestUrl: string;
  status: 'idle' | 'loading' | 'ok' | 'error';
  error?: string;
  cached?: {
    ok: boolean;
    status: number;
    cacheControl: string | null;
    etag: string | null;
    lastModified: string | null;
    remoteEntryName: string | null;
    remoteEntryHash: string | null;
  };
  fresh?: {
    ok: boolean;
    status: number;
    remoteEntryName: string | null;
    remoteEntryHash: string | null;
  };
  hashMatch: boolean | null;
}

const REMOTE_ENTRY_HASH_RE = /remoteEntry[-.]([a-zA-Z0-9_-]+)\.js$/;

function extractHash(name: string | null | undefined): string | null {
  if (!name) return null;
  const match = REMOTE_ENTRY_HASH_RE.exec(name);
  return match ? match[1] : null;
}

async function fetchManifest(
  manifestUrl: string,
  options: { cache: RequestCache; bust?: boolean }
): Promise<{ response: Response; body: MFManifest | null }> {
  const sep = manifestUrl.includes('?') ? '&' : '?';
  const url = options.bust
    ? `${manifestUrl}${sep}_t=${Date.now()}`
    : manifestUrl;
  const response = await fetch(url, { method: 'GET', cache: options.cache });
  let body: MFManifest | null = null;
  if (
    response.ok &&
    response.headers.get('content-type')?.includes('application/json')
  ) {
    try {
      body = (await response.json()) as MFManifest;
    } catch {
      body = null;
    }
  }
  return { response, body };
}

export default function DebugPage() {
  const [menuReady, setMenuReady] = useState(false);
  const [menuError, setMenuError] = useState<string | null>(null);
  const [entries, setEntries] = useState<Array<{ route: string; url: string }>>(
    []
  );
  const [checks, setChecks] = useState<ManifestCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadMenuAndEntries = useCallback(async () => {
    setMenuError(null);
    try {
      await waitForMenu();
      const list = getAllMicroFrontendEntries();
      setEntries(list);
      setMenuReady(true);
      setChecks(
        list.map(({ route, url }) => ({
          route,
          url,
          manifestUrl: `${url.replace(/\/$/, '')}/mf-manifest.json`,
          status: 'idle' as const,
          hashMatch: null,
        }))
      );
    } catch (e) {
      setMenuError(e instanceof Error ? e.message : 'Erro ao carregar menu');
      setEntries([]);
      setChecks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMenuAndEntries();
  }, [loadMenuAndEntries]);

  const runChecks = useCallback(
    async (bust: boolean) => {
      if (entries.length === 0) return;
      setRefreshing(true);

      const nextChecks: ManifestCheck[] = await Promise.all(
        entries.map(async ({ route, url }) => {
          const manifestUrl = `${url.replace(/\/$/, '')}/mf-manifest.json`;
          const base: ManifestCheck = {
            route,
            url,
            manifestUrl,
            status: 'loading',
            hashMatch: null,
          };

          try {
            // cache: 'default' simula o browser do cliente (module federation não força revalidação)
            // cache: 'no-store' + bust vai direto na origem, sem cache algum
            const [cachedResult, freshResult] = await Promise.all([
              fetchManifest(manifestUrl, { cache: 'default' }),
              fetchManifest(manifestUrl, { cache: 'no-store', bust }),
            ]);

            const cachedEntryName =
              cachedResult.body?.metaData?.remoteEntry?.name ?? null;
            const freshEntryName =
              freshResult.body?.metaData?.remoteEntry?.name ?? null;
            const cachedHash = extractHash(cachedEntryName);
            const freshHash = extractHash(freshEntryName);

            return {
              ...base,
              status: 'ok',
              cached: {
                ok: cachedResult.response.ok,
                status: cachedResult.response.status,
                cacheControl:
                  cachedResult.response.headers.get('cache-control'),
                etag: cachedResult.response.headers.get('etag'),
                lastModified:
                  cachedResult.response.headers.get('last-modified'),
                remoteEntryName: cachedEntryName,
                remoteEntryHash: cachedHash,
              },
              fresh: {
                ok: freshResult.response.ok,
                status: freshResult.response.status,
                remoteEntryName: freshEntryName,
                remoteEntryHash: freshHash,
              },
              hashMatch:
                cachedHash !== null && freshHash !== null
                  ? cachedHash === freshHash
                  : null,
            };
          } catch (err) {
            return {
              ...base,
              status: 'error',
              error: err instanceof Error ? err.message : String(err),
              hashMatch: null,
            };
          }
        })
      );

      setChecks(nextChecks);
      setRefreshing(false);
    },
    [entries]
  );

  if (loading) {
    return (
      <div className='container max-w-6xl py-8'>
        <p className='text-muted-foreground'>Carregando menu...</p>
      </div>
    );
  }

  return (
    <div className='container max-w-6xl space-y-6 py-8'>
      <div>
        <h1 className='text-2xl font-semibold tracking-tight'>
          Cache dos MFEs
        </h1>
        <p className='text-muted-foreground mt-1'>
          Compara o <code>mf-manifest.json</code> que o browser do cliente tem
          em cache com o que está na origem agora. Hash diferente = cliente com
          versão velha.
        </p>
      </div>

      {menuError && (
        <Card className='border-destructive/50 bg-destructive/5'>
          <CardContent className='pt-6'>
            <p className='text-destructive font-medium'>Erro no menu</p>
            <p className='text-muted-foreground text-sm mt-1'>{menuError}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Manifests</CardTitle>
          <CardDescription>
            Hash do <code>remoteEntry</code> por MFE. &quot;Forçar&quot;
            adiciona cache bust na requisição para a origem.
          </CardDescription>
          <div className='flex gap-2 mt-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => runChecks(false)}
              disabled={!menuReady || entries.length === 0 || refreshing}
            >
              {refreshing ? 'Verificando...' : 'Verificar'}
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => runChecks(true)}
              disabled={!menuReady || entries.length === 0 || refreshing}
            >
              Forçar atualização
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 && !menuError && (
            <p className='text-muted-foreground text-sm'>
              Nenhum MFE encontrado no menu.
            </p>
          )}
          {entries.length > 0 && (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm border-collapse'>
                <thead>
                  <tr className='border-b'>
                    <th className='text-left py-2 pr-4 font-medium'>Rota</th>
                    <th className='text-left py-2 pr-4 font-medium'>
                      Base URL
                    </th>
                    <th className='text-left py-2 pr-4 font-medium'>
                      Manifest
                    </th>
                    <th className='text-left py-2 pr-4 font-medium'>Status</th>
                    <th className='text-left py-2 pr-4 font-medium'>
                      Hash cliente
                    </th>
                    <th className='text-left py-2 pr-4 font-medium'>
                      Hash origem
                    </th>
                    <th className='text-left py-2 pr-4 font-medium'>
                      Cache-Control
                    </th>
                    <th className='text-left py-2 pr-4 font-medium'>
                      Sincronizado?
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map(c => (
                    <tr key={c.route} className='border-b last:border-0'>
                      <td className='py-2 pr-4 font-mono'>{c.route}</td>
                      <td className='py-2 pr-4 break-all max-w-[180px]'>
                        {c.url}
                      </td>
                      <td className='py-2 pr-4 break-all max-w-[200px]'>
                        {c.manifestUrl}
                      </td>
                      <td className='py-2 pr-4'>
                        {c.status === 'loading' && '...'}
                        {c.status === 'idle' && '-'}
                        {c.status === 'error' && (
                          <span className='text-destructive'>{c.error}</span>
                        )}
                        {c.status === 'ok' &&
                          (c.cached?.ok ? (
                            <span className='text-green-600'>ok</span>
                          ) : (
                            <span className='text-amber-600'>
                              {c.cached?.status ?? '-'}
                            </span>
                          ))}
                      </td>
                      <td className='py-2 pr-4 font-mono text-xs'>
                        {c.cached?.remoteEntryHash ??
                          c.cached?.remoteEntryName ??
                          '-'}
                      </td>
                      <td className='py-2 pr-4 font-mono text-xs'>
                        {c.fresh?.remoteEntryHash ??
                          c.fresh?.remoteEntryName ??
                          '-'}
                      </td>
                      <td className='py-2 pr-4 text-xs max-w-[160px]'>
                        {c.cached && (
                          <span
                            className='block truncate'
                            title={[
                              c.cached.cacheControl,
                              c.cached.etag,
                              c.cached.lastModified,
                            ]
                              .filter(Boolean)
                              .join(' | ')}
                          >
                            {c.cached.cacheControl ?? '-'}
                            {c.cached.etag && ` | ${c.cached.etag}`}
                          </span>
                        )}
                        {!c.cached && c.status !== 'idle' && '-'}
                      </td>
                      <td className='py-2 pr-4'>
                        {c.hashMatch === true && (
                          <span className='text-green-600'>Sim</span>
                        )}
                        {c.hashMatch === false && (
                          <span className='text-amber-600 font-medium'>
                            Não
                          </span>
                        )}
                        {c.hashMatch === null && '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
